import styles from "./Lobby.module.scss";
import LobbyTile from "./LobbyTile";
import {useEffect, useState} from "react";
import {createLobby, getLobbies} from "../../../api/lobby";
import {useNavigate} from "react-router-dom";
import {Lobby, LobbyState} from "../../types/lobby";
import {getCurrentUserId, getCurrentUsername} from "../../../api/authContext";
import ProfileMenu from "./ProfileMenu";
import LeaderboardModal from "./LeaderboardModal";
import CreateLobbyModal from "./CreationModal";


const ENABLE_POLLING = process.env.REACT_APP_ENABLE_LOBBY_POLLING === 'true';

const LobbyHome = () => {
    const navigate = useNavigate();

    const [lobbies, setLobby] = useState<Lobby[]>([]);
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [statsOpen, setStatsOpen] = useState(false);

    const [title, setTitle] = useState<string>("");
    const [password, setPassword] = useState<string>("");

    useEffect(() => {
        getLobbies().then(games => setLobby(games));

        if (!ENABLE_POLLING) {
            console.info('[Lobby] polling disabled via env');
            return;
        }

        const interval = setInterval(() => {
            getLobbies().then(games => setLobby(games));
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    const openModal = () => {
        const username = getCurrentUsername() || "Player";
        setTitle(username + "'s game");
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const onLobbyCreate = (capacity: number = 0) => {
        if (!title.trim() || !password.trim()) {
            return;
        }

        createLobby({title, password, capacity})
            .then(lobby => navigate(`/lobby/${lobby.id}`))
            .catch(_ => getLobbies().then(lobbies => setLobby(lobbies)))
            .finally(() => setModalOpen(false));
    };

    const currentUserId = getCurrentUserId();

    if (!lobbies || Number.isNaN(currentUserId)) {
        return <div className={styles.lobby__loading}>Loading lobbies...</div>;
    }

    const availableLobbies = lobbies
        .filter(lobby => lobby.users.some(user => user.userId === currentUserId)
            || (lobby.state === LobbyState.WAITING && lobby.users.length < lobby.capacity))
        .length;

    const isCreateDisabled = !title.trim() || !password.trim();

    return (
        <>
            <div className={styles.lobby}>
                <div className={styles.lobby__header}>
                    <div className={styles.lobby__info}>
                        <h1 className={styles.lobby__title}>Lobbies</h1>
                        <div className={styles.lobby__meta}>
                            <span className={styles.activeLobbies}>
                                Active lobbies: {availableLobbies} / {lobbies.length}
                            </span>
                            {!ENABLE_POLLING && <span>Refresh page to see new lobbies...</span>}
                        </div>
                    </div>

                    <div className={styles.lobby__controls}>
                        <div className={styles.lobby__controlsTopRow}>
                            <button
                                className={styles.lobby__btn}
                                onClick={() => setStatsOpen(true)}
                            >
                                🏆
                            </button>
                            <ProfileMenu/>
                        </div>
                        <button
                            className={`${styles.lobby__btn} ${styles["lobby__btn--accent"]}`}
                            onClick={openModal}
                        >
                            New Lobby
                        </button>
                    </div>
                </div>

                <div className={styles.lobby__body}>
                    <section className={styles.lobby__listPanel}>
                        <h2 className={styles.lobby__sectionTitle}>Available lobbies</h2>

                        {lobbies.length === 0 ? (
                            <div className={styles.lobby__empty}>
                                <p>No lobbies yet.</p>
                                <p className={styles.lobby__emptyHint}>
                                    Be the first to create a game!
                                </p>
                            </div>
                        ) : (
                            <div className={styles.lobby__list}>
                                {lobbies.map(lobby => (
                                    <LobbyTile key={lobby.id} lobby={lobby}/>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                <CreateLobbyModal
                    open={isModalOpen}
                    title={title}
                    password={password}
                    isCreateDisabled={isCreateDisabled}
                    onTitleChange={setTitle}
                    onPasswordChange={setPassword}
                    onCreate={onLobbyCreate}
                    onClose={closeModal}
                />
            </div>

            <LeaderboardModal
                open={statsOpen}
                onClose={() => setStatsOpen(false)}
                initialTab="games"
            />
        </>
    );
};

export default LobbyHome;
